import { FC, useContext, useEffect, useState } from "react";
import { SidebarContext } from "../../../../../contexts/SidebarContext";
import SearchBox from "../../../../../layouts/SidebarLayout/Header/SearchBox";
import DataTable from "../../../../../shared/components/dataTable";
import useQuery from "../../../../../shared/components/hooks/useQuery";
import Loader from "../../../../../shared/components/loader/linearProgress";
import NoPermissionContent from "../../../../../shared/components/noPermissionBanner";
import { TableViewContainer } from "../../../../../shared/components/tableViewContainer";
import { ZSSelectBox } from "../../../../../shared/components/selectBox";
import { formatDateToDDMMYYYY } from "../../../../../shared/utils/dateFormat";
import { formatAmountINR } from "../../../../../shared/utils/amountFormat";

const PaymentsList: FC = () => {
  const { payload, setPayload } = useContext(SidebarContext);

  const [classOptions, setClassOptions] = useState<any[]>([]);
  const [divisionOptions, setDivisionOptions] = useState<any[]>([]);

  const className = payload.paymentListFilters?.className;
  const divisionValue = payload.paymentListFilters?.division;

  // Clear only paymentListFilters on unmount
  useEffect(() => {
    return () => {
      setPayload((prev: any) => ({
        ...prev,
        paymentListFilters: {
          className: undefined,
          division: undefined,
        },
        skip: 0,
      }));
    };
  }, [setPayload]);


  const { data: classData } = useQuery("/admin/classes");

  useEffect(() => {
    if (classData) {
      const classMap = new Map<string, any>();
      (classData as any).forEach((c: any) => {
        if (!classMap.has(c.displayName)) {
          classMap.set(c.displayName, c);
        }
      });
      const uniqueClasses = Array.from(classMap.values()).map((c: any) => ({
        value: String(c.id),
        label: c.displayName,
      }));
      setClassOptions(uniqueClasses);
    }
  }, [classData]);


  const { data: divisionData } = useQuery(
    className ? `/admin/payments/classes/${className}/divisions` : ""
  );

  useEffect(() => {
    if (Array.isArray(divisionData)) {
      const divisions = (divisionData as any).map((division: string) => ({
        value: division,
        label: division,
      }));
      setDivisionOptions(divisions);

      if (!divisions.find((d: { value: any; }) => d.value === divisionValue)) {
        setPayload((prev: any) => ({
          ...prev,
          paymentListFilters: {
            ...prev.paymentListFilters,
            division: undefined,
          },
        }));
      }
    } else {
      setDivisionOptions([]);
      setPayload((prev: any) => ({
        ...prev,
        paymentListFilters: {
          ...prev.paymentListFilters,
          division: undefined,
        },
      }));
    }
  }, [divisionData, divisionValue, setPayload]);

  // ✅ Fetch payment data
  const { loading, data, fetchData, error } = useQuery("/admin/payments", {
    params: {
      ...payload,
      className: className || undefined,
      division: divisionValue || undefined,
    },
  });

  useEffect(() => {
    fetchData();
  }, [className, divisionValue]);

  const paymentsData = data as any;

  const paymentDisplayColumns = [
    "studentName",
    "class",
    "division",
    "amount",
    "paymentDate",
    "receiptNumber",
    "paymentMode",
  ];

  const titleBarNode = <SearchBox />;

  const renderPaymentCell = (row: any, col: string) => {
    switch (col) {
      case "studentName":
        return row?.studentClass?.student?.name ?? "-";
      case "class":
        return row?.studentClass?.class?.name ?? "-";
      case "division":
        return row?.studentClass?.division ?? "-";
      case "paymentDate":
        return formatDateToDDMMYYYY(row?.paymentDate);
      case "amount":
        return formatAmountINR(row?.amount) ?? "-";
      default:
        return row[col] ?? "-";
    }
  };

  const hasSearch = !!payload.search;
  const hasFilters = !!className || !!divisionValue;
  const hasData = (paymentsData?.items?.length ?? 0) > 0;

  const titleBar = {
    title: "Fees Report",
    headerTitle: "Fees Report",
    titleBarNodes: (paymentsData?.totalItems || payload.search) && titleBarNode,
    subHeading: "",
    rowCount: paymentsData?.totalItems,
  };

  return (
    <TableViewContainer titleBar={titleBar}>
      {/* Filters */}
      <div
        style={{
          display: "flex",
          gap: "1rem",
          marginTop:
            !hasData && !hasSearch && !hasFilters
              ? "1rem"
              : hasSearch
              ? "-3.85rem"
              : hasFilters && !hasData
              ? "1rem"
              : "-3.85rem",
          marginLeft: "2rem",
          transition: "margin-top 0.3s ease-in-out",
        }}
      >
        <div style={{ minWidth: "200px" }}>
          <ZSSelectBox
            fieldProps={{
              id: "className",
              name: "className",
              placeholder: "Select Class",
            }}
            options={classOptions}
            value={className}
            onChange={(value: string) =>
              setPayload({
                ...payload,
                paymentListFilters: {
                  className: value || undefined,
                  division: undefined,
                },
              })
            }
          />
        </div>
        <div style={{ minWidth: "200px" }}>
          <ZSSelectBox
            fieldProps={{
              id: "division",
              name: "division",
              placeholder: "Select Division",
            }}
            options={divisionOptions}
            value={divisionValue}
            onChange={(value: string) =>
              setPayload({
                ...payload,
                paymentListFilters: {
                  ...payload.paymentListFilters,
                  division: value || undefined,
                },
              })
            }
            disabled={!className}
          />
        </div>
        <button
          style={{
            height: 45,
            padding: "1rem",
            backgroundColor: "primary.dark",
            border: "1px solid #ccc",
            borderRadius: 16,
            cursor: "pointer",
            minWidth: "200px",
            color: "#666666",
          }}
          onClick={() => {
            setPayload({
              ...payload,
              paymentListFilters: {
                className: undefined,
                division: undefined,
              },
              skip: 0,
            });
          }}
        >
          Clear Filters
        </button>
      </div>

      {loading && <Loader />}
      {(error as any)?.statusCode === 403 && <NoPermissionContent />}
      {!loading && !error && (
        <DataTable
          entityName="Payments"
          data={paymentsData?.items ?? []}
          displayColumns={paymentDisplayColumns}
          linkColumn="studentName"
          linkUrl="/fees/payments"
          pagination={{ take: payload.take || 10, skip: payload.skip || 0 }}
          setPagination={setPayload}
          totalItems={paymentsData?.totalItems || 0}
          refetchFunction={fetchData}
          renderCell={renderPaymentCell}
        />
      )}
    </TableViewContainer>
  );
};

export default PaymentsList;
